import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import moment from "moment";
import {baseOnSalesDiscountPrice, basePrice, determineFree, discountPrice, discountValue} from './pricingCalculations';
import {cx, priceFormat} from './util';
import styles from "./main.module.scss";
import {translateService, Translate} from '../../services/TranslateService';
import "./payment.scss"
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useDispatch} from 'react-redux';
import Coupon from './Coupon';
import {DataContext} from '../../contexts/dataContext';
import {isCompanyNameValid, isCompanyVatIdValid, useEmailConsent,
  useServiceAgreementValidation, useValidCompanyId, useValidCompanyName,
  checkCustomerInfoValid} from '../../hooks/useCustomerHooks';
import {CustomerAgreeCheckboxInput, CustomerRadioInput,
  CustomerTextInput, UsePointsCheckboxInput} from "../CustomerInput/CustomerInput";
import Modal from "react-modal";
import {cStylesPaymentInfo, cStylesPaymentCreatedRes} from "../../CustomModalStyles";
import {clearPendingPurchase} from "../../services/PurchaseGtagEvent";
import {useClarityStep} from "../../services/clarity/useClarityStep";
import {AddCardInfoLink, AdditionalPaymentInfoContent, SavedCardPaySelectContent} from "./SharedContent";
import { buildCart, GA4_CURRENCY, paymentTypeLabel } from "../../services/ga4Ecommerce";
// import {ReCaptchaDisclaimer} from "../reCaptchaDisclaimer"; // TODO: re-enable when BE reCaptcha is ready

// Notes:
// Quota and 24Rent points cannot be used together (both may be visible if points not selected, but quota is hidden if points usage us selected)
// Payment method should reset to all unselected unless free res (only 1 option for this case)

interface ServiceRowProps {
  included: Boolean,
  label: React.ReactNode;
  content: React.ReactNode;
  discount?: React.ReactNode;
  back : Function;
  scrollLocation: string;
  safeDriveInfo?: Function;
}

export function ServiceRow({ included, label, content, discount, back, scrollLocation, safeDriveInfo }: ServiceRowProps) {
  const dataContext = useContext(DataContext);
  // add safe drive "back button' compensation here

  return <tr>
    <td>{label}
      {safeDriveInfo ?
          <button className={cx("btn btn-info", styles.infoIconBtn)}
                  onClick={() => safeDriveInfo()}>i</button> : null}
    </td>
    <td className={included ? '' : styles.notIncludedText}>
      {included ? content :
          <Translate content={'vehicleSearch1.notIncluded'} />}
    </td>
    <td>

      {safeDriveInfo ? // confirm if this can be discounted --> if not, add to code below
          <div>
            <button className={cx("btn btn-info btn-all", styles.greenAddServicesButton)}
                onClick={() => {
                  const url = 'https://www.24rent.fi/en/safedrivepackage';
                  const newWin = window.open(url, '_blank', 'noopener,noreferrer');
                  if (newWin) newWin.opener = null}}>
              {translateService.translate('vehicleSearch.readMore')}
            </button>
        </div> : null}

      {!dataContext.reservationCreated ? !included ?
          <div scroll-to-item="true" scroll-to="#additionalInsuranceAnchor{vehicle.id}">
            <button className={cx("btn btn-primary btn-cta", styles.orangeAddServicesButton)} onClick={()=>{back(!dataContext.authHashLogin ? 2 : 1, scrollLocation);
              dataContext.sendGa4Event("navigate_back", { back_to: "final_step_additional_services" });
            }}>
              <Translate content={'vehicleSearch1.addService'} />
            </button>
          </div>
          : discount : (dataContext.reservationCreated && included) ? discount : null}
    </td>
  </tr>
}

interface PaymentPropsI {
  back: Function;
  next: Function;
  reservationCreated: boolean;
  setRedirectHome : Function;
  reservationLength : number;
  openSafeDriveInfo: Function;
  showPaymentInProgress: boolean;
  setShowPaymentInProgress: Function;
  escapeWarningOpen?: boolean;
  setEscapeWarningOpen?: Function;
}

// PAYTRAIL (0) is the code sent to BE for any externally-paid reservation.
// When VITE_ENABLE_PAYTRAIL_FOREIGNERS is on, the FE routes foreign customers
// to the Paytrail redirect flow and Finnish customers to the Walley iframe —
// but the BE value is 0 in both cases. STORED_CARD (201) and
// CREDIT_CARD_INPUT_VAL (2000) are FE-only markers that also collapse to 0
// via `determinePaymentMethod` in dataContext.
// QUOTA / FREE / INVOICE are internal BE-handled flows (no external provider).
export enum paymentMethod {
  PAYTRAIL=0,
  CREDIT_CARD_ONCE=1,
  QUOTA=3,
  FREE=6,
  INVOICE=2,
  PAY_AND_STORE_CARD=200,
  STORED_CARD=201,
  CREDIT_CARD_INPUT_VAL=2000, // FE-only marker: resolves to 1 or 200 via determinePaymentMethod.
}

export default function PaymentInfo({ setRedirectHome, reservationCreated, back, next,
                                      reservationLength, openSafeDriveInfo, showPaymentInProgress,
                                      setShowPaymentInProgress, escapeWarningOpen,
                                      setEscapeWarningOpen }: PaymentPropsI) {
  const { rootUrl, services, changeServices, customer, clearSelections, vehicle, oneWayRes, sendGa4Event,
    getReservationDays, searchParameters, changeCustomer, authHashLogin, user, loggedIn, pointsInEurosToBeApplied,
    changePointsInEurosToBeApplied, storedCoupon, changeUser, changeLoggedIn,
    changeVirtualPageInfo, useCompanyInfo, noVatPrices, addSafeDrivePackage, redirectRef, language } = React.useContext(DataContext);
  const dispatch = useDispatch();
  const launcherMode = redirectRef === 'launcher' ? '1' : undefined;
  const eurosPerExtraKm = import.meta.env.VITE_EUROS_PER_EXTRA_KM ?? '';
  const [paymentCountDownTimerInterval, setTimer] = useState<NodeJS.Timeout>();
  const [paymentCountDownTimer, setPaymentCountDownTimer] = useState(" 15:00 ");
  const [paymentCountDownTimerStart, setPaymentCountDownTimerStart] = useState(translateService.translate('vehicleSearch1.translationStartCountDownTimer1'));
  const [paymentCountDownTimerEnd, setPaymentCountDownTimerEnd] = useState(translateService.translate('vehicleSearch1.translationStartCountDownTimer2'));
  const [paymentCountDownTimerNotice, setPaymentCountDownTimerNotice] = useState(translateService.translate('vehicleSearch1.translationStartCountDownTimer3'));
  const [paymentCountDownTimerExpired, setPaymentCountDownTimerExpired] = useState(cx(styles['payment-footer-lastminutenotice'], styles.center));
  const [paymentCountDownTimerClock, setPaymentCountDownTimerClock] = useState(styles.countDownTimerClock);
  const [countDownSeconds, setCountDownSeconds] = useState(900);
  const [countdownExpired, setCountdownExpired] = useState(false);
  let pricingDataType: any = noVatPrices ? vehicle?.pricingDataNoVat : vehicle?.pricingData;
  const couponValid = (((storedCoupon?.valid || vehicle?.vehicleData.class === 0) && pricingDataType?.coupon !== null)
      && !vehicle?.onsale && vehicle?.extraOnsales?.extra !== 1 && !vehicle?.privateData?.private);
  const freeReservation = determineFree(vehicle, noVatPrices, services, reservationLength, couponValid, pointsInEurosToBeApplied);
  const [payFree, setPayFree] = useState(false);
  const [payQuota, setPayQuota] = useState(false);
  const [payPaytrail, setPayPaytrail] = useState(false);
  const [payInvoice, setPayInvoice] = useState(false);
  const [payCreditCard, setPayCreditCard] = useState(false);
  const [paySavedCard, setPaySavedCard] = useState(false);
  const [saveCreditCard, setSaveCreditCard] = useState(false);
  const isForeignCustomer = !!(customer?.unregisteredForeigner || customer?.registeredForeigner);
  const paytrailForForeigners = import.meta.env.VITE_ENABLE_PAYTRAIL_FOREIGNERS === 'true';
  const usePaytrailFlow = paytrailForForeigners && isForeignCustomer;
  const customerHasSavedCard = !!(user?.savedCard && user?.lastFourDigits && user?.cardType);
  const corporateCustomer = (isCompanyNameValid(customer?.companyName) && isCompanyVatIdValid(customer?.companyVatNumber)) ? true : false;
  const {companyName, setCompanyName, companyNameErrorString, setCompanyNameErrorOverride,
    companyNameFieldRef} = useValidCompanyName(user?.company?.name || '', "companyNameField");
  const {companyId, setCompanyId, companyIdErrorString, setCompanyIdErrorOverride,
    companyIdFieldRef} = useValidCompanyId(user?.company?.vatNumber || '', "companyIdField");
  const isCustomerInfoValid = checkCustomerInfoValid(customer);
  const reservationDays = getReservationDays();
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const {agreeErrorString, agree, setPolicyConditionsAndReturn, setLiability, handleServiceAgreeCheckErr,
    acceptFieldRefs } = useServiceAgreementValidation(customer?.agree || false, "acceptField1", true);
  const {emailConsent, setEmailConsentInputs, emailConsentErrorString, handleEmailConsentCheckErr, emailConsent1FieldRef,
    emailConsent2FieldRef} = useEmailConsent({yes: false, no: false},
      "emailConsentField1", "emailConsentField2"); // must set both to unselected on start
  const [ submitValidationErrorString, setSubmitValidationErrorString ] = useState('');
  const [ saveInfoBtnClicked, setSaveInfoBtnClicked ] = useState(false);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState(true); // start with true, check again when clicking 'pay res' button
  const [useTwoFourPoints, setUseTwoFourPoints] = useState(!!(pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0));
  const [additionalPaymentInfoOpen, setAdditionalPaymentInfoOpen] = useState(false);
  const [customerInfoNeeded, setCustomerInfoNeeded] = useState(false);

  let countDownTimer = 900; // 15 minutes in seconds
  try {
    const ridRaw = sessionStorage.getItem('rid-1');
    if (ridRaw) {
      const ridData = JSON.parse(ridRaw);
      if (ridData?.resCreateTime) {
        const elapsed = Math.floor((Date.now() - new Date(ridData.resCreateTime).getTime()) / 1000);
        if (elapsed > 0) countDownTimer = Math.max(900 - elapsed, 0);
      }
    }
  } catch { /* keep default 900 */ }
  let paymentPending = sessionStorage.getItem('paymentPending') ?? undefined;
  const paymentReselection = !!reservationCreated && !sessionStorage.getItem('paymentProvider');
  let pageView = reservationCreated && services.paymentMethod !== paymentMethod.PAYTRAIL
      && services.paymentMethod !== paymentMethod.CREDIT_CARD_INPUT_VAL
      && services.paymentMethod !== paymentMethod.STORED_CARD
      && !paymentPending;
  Modal.setAppElement('body');

  useClarityStep(pageView ? 'resultSuccessQuotaFree' : 'resSummary', {
    city: searchParameters?.location?.formatted_address || searchParameters?.pickupCity || '',
    start: searchParameters?.startDate?.toString() || '',
    end: searchParameters?.endDate?.toString() || '',
    loggedIn: loggedIn ?? false,
    oneway: oneWayRes,
    paymentMethod: services.paymentMethod, // will be missing from sent data until method selected --> as 'undefined' is unselected default
    safeDrive: addSafeDrivePackage ?? false
  });

  useEffect(() => {
    if (!reservationCreated && sessionStorage.getItem('use24Points')) {
      // remove session stored 24 points indicator if exists
      // (used for payment summary after res created)
      sessionStorage.removeItem('using24Points');
    }
  }, [reservationCreated])

  // GA4 begin_checkout — fire once when the summary/checkout screen is reached
  // (the effect re-runs on re-render, so guard with a ref).
  const beginCheckoutFired = React.useRef(false);
  useEffect(() => {
    if (pageView) {
      changeVirtualPageInfo('quotaFreeSuccess');
    } else {
      changeVirtualPageInfo('summary');
      if (vehicle && !beginCheckoutFired.current) {
        beginCheckoutFired.current = true;
        const cart = buildCart(vehicle, services, reservationLength, noVatPrices);
        sendGa4Event("begin_checkout", {
          currency: GA4_CURRENCY,
          value: cart.value,
          coupon: storedCoupon?.code || undefined,
          items: cart.items
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageView, changeVirtualPageInfo]);

  // GA4 add_payment_info — fire ONCE per checkout, on the first payment-method
  // selection. paymentMethod has a default, so skip the initial mount run; the
  // fired-once guard then prevents re-firing if the user switches methods.
  const addPaymentInfoFirstRun = React.useRef(true);
  const addPaymentInfoFired = React.useRef(false);
  useEffect(() => {
    if (addPaymentInfoFirstRun.current) {
      addPaymentInfoFirstRun.current = false;
      return;
    }
    if (addPaymentInfoFired.current) return;
    if (vehicle && services?.paymentMethod !== undefined && services?.paymentMethod !== null) {
      addPaymentInfoFired.current = true;
      const cart = buildCart(vehicle, services, reservationLength, noVatPrices);
      sendGa4Event("add_payment_info", {
        currency: GA4_CURRENCY,
        value: cart.value,
        coupon: storedCoupon?.code || undefined,
        payment_type: paymentTypeLabel(services.paymentMethod),
        items: cart.items
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services?.paymentMethod]);

  useEffect(() => {
    // reset or set company name and id if payment method changes
    if (services.paymentMethod === paymentMethod.QUOTA && !corporateCustomer ) {
      setCompanyIdErrorOverride(false);
      setCompanyNameErrorOverride(false);

    } else if (companyIdErrorString !== '' || companyNameErrorString !== '') {
      setCompanyIdErrorOverride(true);
      setCompanyNameErrorOverride(true);
    }

    if (services.paymentMethod !== paymentMethod.QUOTA && (services.quotaCompanyName || services.quotaCompanyId)) {
      setCompanyId('');
      setCompanyName('');
      changeServices({...services, quotaCompanyName: undefined, quotaCompanyId: undefined});
    }
  }, [services, services.quotaCompanyId, services.quotaCompanyName, companyIdErrorString,
    companyNameErrorString, changeServices, corporateCustomer, setCompanyIdErrorOverride, setCompanyNameErrorOverride,
  setCompanyName, setCompanyId]);

  // check if invoice payment method can be used
  let canUseInvoice = false;
  if (customer?.mayUseBankTransferAnytime &&
      moment(searchParameters?.startDate, 'L').diff(moment(), 'days') >=
      (customer?.relativeBankTransferDueDate < 0 ? (customer?.relativeBankTransferDueDate * -1) : 999)) {
    canUseInvoice = true;
  } else {
    canUseInvoice = false;
  }

  const service = (discount: boolean , free: boolean) => {
    if (free && (discount || (pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0))) {
      changeServices({...services, paymentMethod: paymentMethod.FREE });
    }
    if (!free) {
      changeServices({...services, paymentMethod: undefined}); // this should always be undefined now since user has option to add card or use saved card as well
    }
  }

  useEffect(() => {
    service(!!couponValid, freeReservation)
  }, [couponValid, freeReservation]);

  useEffect(() => {
    setPayFree(services.paymentMethod===paymentMethod.FREE)
    setPayQuota(services.paymentMethod===paymentMethod.QUOTA)
    setPayPaytrail(services.paymentMethod===paymentMethod.PAYTRAIL)
    setPayInvoice(services.paymentMethod===paymentMethod.INVOICE);
    setPayCreditCard(services.paymentMethod===paymentMethod.CREDIT_CARD_INPUT_VAL);
    setPaySavedCard(services.paymentMethod===paymentMethod.STORED_CARD);
  }, [services]);

  useEffect(() => {
    if (saveCreditCard && !payCreditCard) {
      setSaveCreditCard(false);
      changeServices({...services, saveNewCreditCard: false});
    }
  }, [payCreditCard, saveCreditCard]);

  useEffect(() => {
    if(!paymentMethodSelected && services.paymentMethod !== undefined) {
      setPaymentMethodSelected(true);
    }
  }, [paymentMethodSelected, services.paymentMethod])

  useEffect(()  => {
    if (payInvoice && !canUseInvoice) { // resets payment methods if invoice option selected but no longer available
      if (freeReservation) return; // case where invoice no longer available due to date changes + res is free
      changeServices({...services, "paymentMethod": undefined})
    }
  }, [reservationDays, payInvoice, canUseInvoice, freeReservation])

  useEffect(() => {
    if (!reservationCreated) return;
    if (paymentCountDownTimerInterval)
      clearInterval(paymentCountDownTimerInterval);
    let timer = setInterval(() => {
      if (countDownTimer >= 0) {
        let minutes = Math.floor((countDownTimer % 3600) / 60);
        let seconds = Math.floor(countDownTimer % 60);

        let stringMinutes = minutes.toString();
        let stringSeconds = seconds.toString();
        if (minutes < 10) {
          stringMinutes = '0' + minutes;
        }
        if (seconds < 10) {
          stringSeconds = '0' + seconds;
        }
        setPaymentCountDownTimer(" " + stringMinutes + ":" + stringSeconds + " ");
        setPaymentCountDownTimerClock(styles.countDownTimerClock);
        setPaymentCountDownTimerNotice(translateService.translate('vehicleSearch1.translationStartCountDownTimer3'));
        setCountDownSeconds(countDownTimer);
        countDownTimer -= 1;
      } else {
        clearInterval(paymentCountDownTimerInterval);
        setPaymentCountDownTimer(translateService.translate('vehicleSearch1.translationStartCountDownTimer4'));
        setPaymentCountDownTimerStart('');
        setPaymentCountDownTimerEnd('');
        setPaymentCountDownTimerNotice(translateService.translate('vehicleSearch1.translationStartCountDownTimer5'));
        setPaymentCountDownTimerClock(styles.countDownTimerClockExpired);
        setPaymentCountDownTimerExpired(cx(styles['payment-footer-lastminutenotice-expired'], styles.center));
        setCountdownExpired(true);
        sendGa4Event("payment_timeout");
      }
    }, 1000);
    setTimer(timer);
    return () => { clearInterval(timer) };
  }, [reservationCreated]);

  useEffect(()=> {
    if (freeReservation) {
      changeServices({...services, "paymentMethod": paymentMethod.FREE})
    } else {
      // Pre-select the Walley merged radio when it's the only option this customer
      // can actually pick. "Only option" = not free, not eligible for the quota
      // radio (mayUseQuota + useCompanyInfo), not eligible for invoice (canUseInvoice
      // + corporateCustomer), and no saved card available.
      const quotaAvailable = !!(customer?.mayUseQuota && useCompanyInfo && !useTwoFourPoints);
      const invoiceAvailable = canUseInvoice && corporateCustomer;
      const savedCardAvailable = !!(user?.savedCard && user?.lastFourDigits && user?.cardType);
      const onlyWalleyAvailable = !quotaAvailable && !invoiceAvailable && !savedCardAvailable;
      changeServices({
        ...services,
        paymentMethod: onlyWalleyAvailable ? paymentMethod.PAYTRAIL : undefined,
      });
    }

    if(user?.emailConsent) {
      // do not ask for email consent if user previous chose to receive emails
      setEmailConsentInputs(true, "yes");
    }
  },[])

  useEffect(() => {
    changeCustomer({...customer, agree: agree,
      agree2: agree,
      emailConsent: emailConsent.yes ? "true" : "false",
      fairAccept: agree
    })

    if(agree && submitValidationErrorString) {
      setSubmitValidationErrorString('');
    }
  }, [agree, emailConsent, submitValidationErrorString])

  if(!vehicle){
    return(
        <div className={cx(styles.r24FullWidth)}>
          <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
            <span> Vehicle Information Not Found </span>
          </div>
        </div>
    )
  }

  const onCreateReservation = (bankName?: string) => {
    setPaymentSubmitted(true);

    const checkValidationFields = () => {
      // uses the hook functions to check errors and return component names for autoscroll if err exists
      if(handleServiceAgreeCheckErr()) { return handleServiceAgreeCheckErr() }
      if(handleEmailConsentCheckErr()) { return handleEmailConsentCheckErr() }
      return null
    }

    let conditionsFieldError = paymentReselection ? null : checkValidationFields();
    if (conditionsFieldError) {
      let inputField = document.querySelector('[name="' + conditionsFieldError + '"]');
      inputField?.scrollIntoView({behavior: "smooth", block: "center"})
      setSubmitValidationErrorString(translateService.translate("vehicleSearch1.invalidSaveInfo"));
    } else {
      setSubmitValidationErrorString('');
      next(bankName);
    }
    if (useTwoFourPoints) {sessionStorage.setItem('using24Points', 'true')}
  }

  const twoFourPointsText = (returnCase: number) => {
    let points = user?.twofourPoints ? user?.twofourPoints : 0;
    let pointsDiscount = points * 0.01;

    if (returnCase === 1) {
      return <> <Translate with={{ type: points }} content={'payment.youHaveTwoFourPoints'}/>
        <Translate content={'payment.cannotUsePoints'}/>
      </>
    } else {
      let currentResPrice = vehicle.onsale ? baseOnSalesDiscountPrice(vehicle, noVatPrices, services, reservationLength) :
          couponValid ? discountPrice(vehicle, noVatPrices, services, reservationLength) : basePrice(vehicle, noVatPrices, services, reservationLength);
      let maxPointsDiscount = Math.floor(Math.round(currentResPrice * 100) / 2) / 100;
      let discountToBeApplied = pointsDiscount > maxPointsDiscount ? maxPointsDiscount : pointsDiscount;

      if (useTwoFourPoints && pointsInEurosToBeApplied !== discountToBeApplied) {
        changePointsInEurosToBeApplied(discountToBeApplied)
      } else if (!useTwoFourPoints && pointsInEurosToBeApplied !== 0) {
        changePointsInEurosToBeApplied(0)
      }

      if (returnCase === 2) {
        return <> <Translate with={{ type: points }} content={'payment.youHaveTwoFourPoints'}/>
          <Translate with={{ type: priceFormat(discountToBeApplied) }} content={'payment.discountIfUsingPoints'}/>
        </>
      }
      if (returnCase === 3) {
        return <> <Translate with={{ type: points }} content={'payment.youHaveTwoFourPoints'}/>
          <Translate with={{ type: priceFormat(discountToBeApplied) }} content={'payment.discountIfUsingPoints'}/>
          <Translate content={'payment.quotaRestrictedWithPoints'}/>
        </>
      }
    }
  }

  return <>
    <div className={cx(styles.r24FullWidth)}>
      <Modal isOpen={customerInfoNeeded} style={cStylesPaymentInfo}>
        <div style={{textAlign: "center", justifyContent: "center"}}>
          <h3> {translateService.translate('payment.customerInfoMissing')} </h3>
          <br/>
          <button className='btn btn-primary btn-outline-primary btn-cta mt-2 w-50 m-auto'
              onClick={() => {setCustomerInfoNeeded(false);
            if (authHashLogin) {
              changeLoggedIn(loggedIn, 'remove'); // remove auth hash login so that customer info step is visible
              } else {back(1, 'customerInformation')}}} >
            {translateService.translate('payment.fillMissingInfo')}
          </button>
        </div>
      </Modal>
      <Modal isOpen={additionalPaymentInfoOpen} style={cStylesPaymentInfo}>
        <AdditionalPaymentInfoContent setModalClose={setAdditionalPaymentInfoOpen} />
      </Modal>
      {paymentReselection &&
        <Modal isOpen={!!escapeWarningOpen} style={cStylesPaymentCreatedRes}>
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: "center", justifyContent: "center" }}>
            <span>
              {translateService.translate('paymentCallbackScreens.exitPaymentWarning')}
            </span>
            <button onClick={() => {
                sendGa4Event('CustomerDiscardedReservation');
                clearPendingPurchase();
                clearSelections();
                dispatch({type:"CLEAR_SELECTIONS",payload:null});
                setRedirectHome(true);
              }}
              className='btn btn-primary btn-outline-primary btn-all m-auto mt-3 w-50'>
              {translateService.translate('paymentCallbackScreens.yesReturnHome')}
            </button>
            <button onClick={() => { sendGa4Event("navigate_back", { back_to: "back_from_payment" }); setEscapeWarningOpen?.(false) }}
              className='btn btn-primary btn-outline-primary btn-all m-auto mt-3 w-50'>
              {translateService.translate('vehicleSearch.close')}
            </button>
          </div>
        </Modal>
      }
      <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
        <table className={styles.r24PriceTable} style={{width: '100%'}}>
          <thead>
          <tr>
            <th><Translate content={'vehicleSearch1.services'} /></th>
            <th><Translate content={'vehicleSearch1.price'} /></th>
            <th >{(couponValid || vehicle.onsale ||
                (pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0)
                || (vehicle?.privateData?.vip)) ?
                <Translate content={'vehicleSearch1.discountedPrice'}/> : null }</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td> {vehicle.vehicleModelData.manufacturer} {vehicle.vehicleModelData.name} </td>
            <td> {priceFormat(pricingDataType.base.totalPrice / 100)} € </td>
            <td><strong>
              {vehicle.onsale ? // #TODO: ADD NO VAT OPTION FOR THIS AS WELL
                  (priceFormat( (vehicle.onsale.price*vehicle.onsale.discountmultiplier) / 100) + ' €')
                  :
                  (couponValid && priceFormat((pricingDataType.withCoupon?.totalPrice ?? 1) / 100) +' €')
              }</strong>
            </td>
          </tr>
          {
            oneWayRes ?
                <>
                  <tr>
                    {services.kmRange === 100 &&
                        <>
                          <td> <Translate content={'vehicleSearch1.onewayKmIncludedService'} />
                            {100 * reservationLength} km.&nbsp;
                          </td>
                          <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                          {couponValid &&
                              <td>
                                <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>,&nbsp;
                                <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                              </td>
                          }
                          {!couponValid && <td></td>}
                        </>

                    }
                    {(services.kmRange === 0) &&
                        <td> <Translate content={'vehicleSearch1.unlimited'} /> km </td>}
                    {services.kmRange === 0 && <>
                      <td>{priceFormat(pricingDataType.base.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                      </td>
                      {couponValid && <td>
                        { priceFormat(pricingDataType.withCoupon!.unlimitedKilometers/100 * reservationLength)} &nbsp;€
                      </td>}
                    </>}
                  </tr>
                </>

                : <>
                  <tr>
                    {(services.kmRange === 100 || services.kmRange === 400) &&
                        <td>{services.kmRange * reservationLength /*make 0 decimals*/} km</td>}
                    {(services.kmRange === 0 || services.kmRange === 1000) &&
                        <td> <Translate content={'vehicleSearch1.unlimited'} /> km </td>}
                    {services.kmRange === 100 &&
                        <>
                          <td><Translate content={'vehicleSearch1.includedInPrice'} />, <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km</td>
                          {couponValid &&
                              <td>
                                <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>,&nbsp;
                                <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                              </td>
                          }
                        </>}
                    {services.kmRange === 400 && <>
                      <td>{priceFormat(pricingDataType.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€,&nbsp;
                        <Translate content={'vehicleSearch1.pricePerExtraKm'} /> {eurosPerExtraKm} &nbsp;€ / km
                      </td>
                      {couponValid &&
                          <td >
                            <strong>
                              { priceFormat(pricingDataType.base.pricePerKilometer/100 *
                                  Math.max(services.kmRange - 100, 0) * reservationLength) } &nbsp;€
                            </strong>,&nbsp; <Translate content={'vehicleSearch1.pricePerExtraKm'}/> {eurosPerExtraKm} &nbsp;€ / km
                          </td>}
                    </>}
                    {services.kmRange === 1000 && <>
                      <td>{priceFormat(pricingDataType.base.pricePerKilometer / 100 * (services.kmRange - 100) * reservationLength)} &nbsp;€
                      </td>
                      {couponValid && <td>
                        <strong>
                          { priceFormat(pricingDataType.base.pricePerKilometer/100 *
                              Math.max(services.kmRange - 100, 0) * reservationLength) } &nbsp;€
                        </strong>
                      </td>}
                    </>}
                    {services.kmRange === 0 && <>
                      <td>{priceFormat(pricingDataType.base.unlimitedKilometers / 100 * reservationLength)} &nbsp;€
                      </td>
                      {couponValid && <td>
                        { priceFormat(pricingDataType.withCoupon!.unlimitedKilometers/100 * reservationLength)} &nbsp;€
                      </td>}
                    </>}
                    {!couponValid && <td></td>}
                  </tr>
                </>
          }

          {services.safeDrivePackage ?
              <ServiceRow included={services.safeDrivePackage} back={back} scrollLocation={''}
                                                    label={<Translate content={'vehicleSearch1.safeDrivePackage'}/>}
                                                    content={<>{priceFormat(pricingDataType.base.safeDrivePackage / 100 * services.safeDriveReservationLength)} €</>}
                                                    discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.safeDrivePackage/100 * services.safeDriveReservationLength)} €</strong>}
                                                    safeDriveInfo={openSafeDriveInfo}/> : null}

          <ServiceRow included={services.insurance.includes('1030')} back={back} scrollLocation={'insurancesAndDeductibles'}
                      label={<>
                        <Translate content={'vehicleSearch1.fairdeductible'}/>&nbsp;
                        {priceFormat(vehicle.vehicleData.deductible.highDeductibleValue! / 100 * (services.export?4:1))} &nbsp;€
                      </> }
                      content={<><Translate content={'vehicleSearch1.includedInPrice'}/></>}
          />

          <ServiceRow included={services.insurance.includes('1053')} back={back} scrollLocation={'insurancesAndDeductibles'}
                      label={<><Translate content={'vehicleSearch.services.midDeductionText'} />&nbsp;
                        {priceFormat(vehicle.vehicleData.deductible.midDeductibleValue! / 100 * (services.export?4:1))} €</>}
                      content={<>{priceFormat(pricingDataType.base.midDeductible / 100 * services.insuranceReservationLength)} €</>}
                      discount={couponValid &&
                          <strong>{ priceFormat(pricingDataType.withCoupon!.midDeductible/100 * services.insuranceReservationLength)} €</strong>}
          />

          <ServiceRow included={services.insurance.includes('1028')} back={back} scrollLocation={'insurancesAndDeductibles'}
                      label={<>
                        <Translate content={'vehicleSearch1.deductible'} />&nbsp;
                        {priceFormat(vehicle.vehicleData.deductible.lowDeductibleValue! / 100 * (services.export?4:1))} €</>}
                      content={<>{priceFormat(pricingDataType.base.lowDeductible / 100 * services.insuranceReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.lowDeductible/100 * services.insuranceReservationLength)} €</strong>}
          />

          <ServiceRow included={services.windshield} back={back} scrollLocation={'additionalInsurances'}
                      label={<Translate content={'vehicleSearch1.windshieldInsurance'} />}
                      content={<>{priceFormat(pricingDataType.base.windshieldInsurance / 100 * services.windshieldReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.windshieldInsurance/100 * services.windshieldReservationLength)} €</strong>}
          />

          <ServiceRow included={services.tireInsurance}  back={back} scrollLocation={'additionalInsurances'}
                      label={<Translate content={'vehicleSearch1.tireInsurance'} />}
                      content={<>{priceFormat(pricingDataType.base.tireInsurance / 100 * services.tireInsuranceReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.tireInsurance/100 * services.tireInsuranceReservationLength)} €</strong>}
          />

          {!oneWayRes ?
              <ServiceRow included={services.cancellationInsurance} back={back} scrollLocation={'additionalInsurances'}
                          label={<Translate content={'vehicleSearch1.cancellationInsurance'} />}
                          content={<>{priceFormat(pricingDataType.base.cancellationInsurance / 100 * services.cancellationInsuranceReservationLength)} €</>}
                          discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.cancellationInsurance/100 * services.cancellationInsuranceReservationLength)} €</strong>}
              /> : null }

          <ServiceRow included={services.additionalDriver}   back={back} scrollLocation={'additionalDriver'}
                      label={<Translate content={'vehicleSearch1.additionalDriver'} />}
                      content={<>{priceFormat(pricingDataType.base.additionalDriver / 100 * services.additionalDriverReservationLength)} €</>}
                      discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.additionalDriver/100 * services.additionalDriverReservationLength)} €</strong>}
          />

          {(vehicle.vehicleData.hasHitch && services.hook) &&
              <ServiceRow included={services.hook} back={back}  scrollLocation={'additionalServices'}
                          label={<Translate content={'vehicleSearch1.hook'} />}
                          content={<>{priceFormat(pricingDataType.base.towHitch / 100 * services.hookReservationLength)} €</>}
                          discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.towHitch/100 * services.hookReservationLength)} €</strong>}/>
          }

          {services.hasHandtruck &&
              <ServiceRow included={services.handTruck} back={back} scrollLocation={'additionalServices'}
                          label={<Translate content={'vehicleSearch1.handTruck'} />}
                          content={<Translate content={'vehicleSearch1.includedInPrice'} />}
                          discount={couponValid && <strong><Translate content={'vehicleSearch1.includedInPrice'}/></strong>}/>
          }

          {(vehicle.vehicleData.exportPermits.balticStates ||
              vehicle.vehicleData.exportPermits.scandinavia || vehicle.vehicleData.exportPermits.europe) ?
              <ServiceRow included={services.export} back={back}  scrollLocation={'export'}
                          label={<Translate content={'vehicleSearch1.export'} />}
                          content={<>{priceFormat(pricingDataType.base.export / 100 * services.exportReservationLegth)} €</>}
                          discount={couponValid && <strong>{ priceFormat(pricingDataType.withCoupon!.export/100 * services.exportReservationLegth)} €</strong>}/>
              : null
          }

          {services.co2compensation === '' &&
              <tr>
                <td><Translate content={'vehicleSearch1.co2Compensation'} /></td>
                <td className={styles.notIncludedText}><Translate content={'vehicleSearch1.notIncluded'} /></td>
                <td>
                  { !reservationCreated ?
                      <div scroll-to-item="true" scroll-to="#exportAnchor{vehicle.id}">
                        <button className={cx("btn btn-primary btn-cta", styles.orangeAddServicesButton)} onClick={(e)=>{back(2,'co2compensation0{vehicle.id}');
                          sendGa4Event("navigate_back", { back_to: "final_step_additional_services" })}}>
                          <Translate content={'vehicleSearch1.addService'} />
                        </button>
                      </div>
                      : null
                  }

                </td>
              </tr>
          }
          {services.co2compensation === '1047' &&
              <tr>
                <td><Translate content={'vehicleSearch1.miniCompensation'} /></td>
                <td>{priceFormat(pricingDataType.base.hundredTwentyFiveGramsCOTwo! / 100 * services.co2CompensationReservationLength)} €</td>
                {couponValid &&
                    <td>
                      <strong>{ priceFormat(pricingDataType.withCoupon!.hundredTwentyFiveGramsCOTwo!/100 * services.co2CompensationReservationLength)} €</strong>
                    </td>}
              </tr>
          }
          {services.co2compensation === '1048' &&
              <tr>
                <td><Translate content={'vehicleSearch1.basicCompensation'} /></td>
                <td>
                  { priceFormat(pricingDataType.base.fiveHundredGramsCOTwo/100 * services.co2CompensationReservationLength)}
                </td>
                {couponValid &&
                    <td>
                      <strong>{ priceFormat(pricingDataType.withCoupon!.fiveHundredGramsCOTwo/100 * services.co2CompensationReservationLength)} €</strong>
                    </td>}
              </tr>
          }
          {services.co2compensation === '1049' &&
              <tr>
                <td>
                  <Translate content={'vehicleSearch1.overCompensation'} />
                </td>
                <td>
                  {priceFormat(pricingDataType.base.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                </td>
                {couponValid &&
                    <td>
                      {priceFormat(pricingDataType.withCoupon!.sevenHundredGramsCOTwo / 100 * services.co2CompensationReservationLength)}
                    </td>
                }
              </tr>
          }
          {couponValid &&
              <tr>
                <td> <Translate content={'vehicleSearch1.discount'}/> {vehicle.vehicleData.class === 0 ? ' (SmartSaver)' : null}</td>
                <td> 0 €</td>
                <td>
                  <strong> -{priceFormat(discountValue(vehicle, noVatPrices, services,reservationLength)) } €</strong>
                </td>
              </tr> }

          { useTwoFourPoints && pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0 ?
              <tr>
                <td><strong>{translateService.translate('payment.twoFourPoints')}</strong></td>
                <td> 0 €</td>
                <td><strong>-{priceFormat(pointsInEurosToBeApplied ?? 0)} € </strong></td>
              </tr>
              : null}
          <tr>
          <td><strong>
              <Translate with={{
                type: noVatPrices // TODO: Add vat type change on click here? or maybe leave this as none clickable text
                    ? translateService.translate('vehicleSearch.vatPercent0') :
                    translateService.translate('vehicleSearch.vatPercent25')
              }} content='vehicleSearch1.totalVat'/>
            </strong></td>
            <td>
              {priceFormat(basePrice(vehicle, noVatPrices, services, reservationLength))} €
            </td>
            <td>
              { vehicle.onsale ?
                    <strong>{priceFormat(baseOnSalesDiscountPrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} €</strong>
                    : ( couponValid ?
                            <strong>{priceFormat(discountPrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} €</strong>
                            : pointsInEurosToBeApplied && pointsInEurosToBeApplied > 0 ?
                                <strong>{priceFormat(basePrice(vehicle, noVatPrices, services, reservationLength, pointsInEurosToBeApplied))} € </strong>
                                : null
                    )
              }
            </td>
          </tr>

          </tbody>
        </table>
      </div>
      {user?.twofourPoints && user?.twofourPoints > 0 && !reservationCreated ?
          <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
            { user?.twofourPoints < 500 ?
                  <div className={styles.twoFourPointsRow}>
                    <span className={cx(styles.r24CheckBoxInputLabel)}>
                      {twoFourPointsText(1)}
                    </span>
                  </div>
                  : <div className={styles.twoFourPointsRow}>
                    <span className={cx(styles.r24CheckBoxInputLabel)}>
                      {customer?.mayUseQuota && useCompanyInfo?
                          <span className={cx(styles.r24CheckBoxInputLabel)}> {twoFourPointsText(3)} </span>
                          : twoFourPointsText(2)}
                    </span>
                    <UsePointsCheckboxInput label={<span> {translateService.translate('payment.useTwoFourPointsNow')} </span>}
                                            disabled={!!(reservationCreated || paymentPending)}
                                            checked={useTwoFourPoints} setValue={setUseTwoFourPoints}
                                            fieldName={'use24RentPoints'}/>
                  </div>
            }
          </div> : null
      }
      {!vehicle.onsale && !reservationCreated && !oneWayRes && !vehicle.privateData?.vip
          && vehicle.vehicleData.class !== 0 && <Coupon carId={vehicle.id}/>}

      <>
        {(reservationCreated && services.paymentMethod !== paymentMethod.PAYTRAIL && services.paymentMethod !== paymentMethod.CREDIT_CARD_INPUT_VAL
        && services.paymentMethod !== paymentMethod.STORED_CARD && !paymentPending) ?
            (<div className={cx(styles.r24FullWidth, styles.r24Padding10, styles.r24FullWidthFlexPageSelection)}>
                  <div className={cx(styles.r24ChildFlex1)}>
                    <div className={cx(styles.borderAroundLightRed, styles.r24BottomMargin10)}>
                      <div className={cx(styles.r24FullWidth , styles.r24TopBottomMargin10)}>
                        <h3 className={cx("text-center", styles.r24ReservationSuccess)}>
                          <Translate with={{ resNum: reservationCreated }} content='vehicleSearch1.translationReservationDone' />
                        </h3>
                        <p className={cx("text-center", styles.r24ReservationSuccess)}>
                          <Translate  content={services.paymentMethod===paymentMethod.QUOTA ? 'vehicleSearch1.translationAfterQuota' : 'vehicleSearch1.translationAfterReservation'}/>
                        </p>
                        <br/>
                        <p className={cx("text-center", styles.r24ReservationSuccess)}>
                          <Translate  content={'vehicleSearch1.successfulQuotaOrFreeRes1'}/>
                          <a href={launcherMode ? 'rentapp://launch-screen/Home'
                              : 'https://m.24rent.fi'}> <Translate content={ launcherMode ? 'paymentCallbackScreens.goToLauncher'
                              : 'vehicleSearch1.successfulQuotaOrFreeRes2'}/> 
                          </a>
                          <Translate  content={'vehicleSearch1.successfulQuotaOrFreeRes3'}/>
                        </p>
                      </div>
                      <div className={cx(styles.r24FullWidth, styles.r24TopBottomMargin10)}>
                      </div>
                    </div>
                    <button className={cx("btn btn-cta", styles.orangeButton)}
                            onClick={() => {window.location.href = launcherMode ? 
                                'rentapp://launch-screen/Home' : 'https://m.24rent.fi'}} >
                      <Translate content={ launcherMode ? 'paymentCallbackScreens.goToLauncher'
                          : 'vehicleSearch1.goToMatkaan'}/>
                    </button>
                    <br/>
                  </div>
                </div>
            ) :
            (<>
              {reservationCreated && (
              <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable, styles.r24BottomMargin10)}>
                <div className={paymentCountDownTimerExpired}>
                  <span className={styles.lastminutecounternotice1}>
                    {countDownSeconds > 0 ? <>
                      <strong className={styles.countdownTimeLine}>{countDownSeconds % 2 === 0 ? '⏳' : '⌛'}<span className={paymentCountDownTimerClock}>{paymentCountDownTimer}</span></strong> &nbsp;{translateService.translate('paymentCallbackScreens.paymentCountdownRemaining')}
                      <br />
                      {translateService.translate('paymentCallbackScreens.paymentCountdownText')}
                    </> : <>
                      {paymentCountDownTimerStart}
                      <span className={paymentCountDownTimerClock}>{paymentCountDownTimer}</span>
                      {paymentCountDownTimerEnd}
                    </>}
                  </span>
                  <div className={styles.lastminutecounternotice2}>
                    {countDownSeconds > 0
                      ? translateService.translate('paymentCallbackScreens.paymentCountdownNotice')
                      : paymentCountDownTimerNotice}
                  </div>
                </div>
              </div>
              )}
              {countdownExpired ? (
                <div className='d-flex flex-column align-items-center mt-3 mb-3'>
                    <span className='text-center'>
                        {translateService.translate('paymentCallbackScreens.paymentExpiredText')}
                    </span>
                    <button className="btn btn-primary btn-outline-primary btn-cta mt-3"
                            onClick={() => {
                                clearSelections();
                                dispatch({type:"CLEAR_SELECTIONS",payload:null});
                                setRedirectHome(true);
                            }}>
                        {translateService.translate('paymentCallbackScreens.resCancelledBtn1')}
                    </button>
                </div>
              ) : null}
              <div className={cx(styles.r24FullWidth, styles.noBorderAroundTable)} hidden={countdownExpired}>
                <form className={styles['payment-modal']}>
                  <div className={cx(styles.r24FullWidth, styles.r24AlingCenter,styles.r24Padding10,)}
                       style={{border: paymentMethodSelected ? "none" : "2px dotted red"}}
                       onChange={ (e) => {
                         if((e.target as HTMLInputElement)?.name === 'saveCreditCardSelection') {
                           changeServices({...services, saveNewCreditCard: (e.target as HTMLInputElement)?.checked});
                           return;
                         }
                         if((e.target as HTMLInputElement)?.name === 'companyNameField') {
                           changeServices({...services, quotaCompanyName: (e.target as HTMLInputElement)?.value});
                           return;
                         }
                         if((e.target as HTMLInputElement)?.name === 'companyIdField') {
                           changeServices({...services, quotaCompanyId: (e.target as HTMLInputElement)?.value});
                           return;
                         }
                         changeServices({...services, paymentMethod: Number(e.target? (e.target as HTMLInputElement).value : undefined)})
                       }}>
                    <div className={cx(styles.r24FullWidth,"mb-3")} id="selectPaymentMethod">
                      <span className={styles.creditCardInformation}><Translate content={'vehicleSearch1.payment'} /></span>
                    </div>

                    <div className={cx(styles.r24FullWidth, styles.r24Padding10, styles.paymentSelectionError)}
                         hidden={paymentMethodSelected}>
                      {translateService.translate('payment.paymentRequired')} </div>

                    {(customer?.mayUseQuota && useCompanyInfo && !freeReservation && !useTwoFourPoints && !paymentReselection) ?
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                          <div className={styles.paymentRow}>
                            <input type="radio" name="paymentMethodsSelection" id="quotaButton{vehicle.id}"
                                   checked={payQuota} readOnly value={paymentMethod.QUOTA} className={styles.r24CheckboxInput}/>
                            <label htmlFor="quotaButton{vehicle.id}" className={cx(styles.r24CheckBoxInputLabel)}>
                              <Translate content={'vehicleSearch1.quota'} />
                            </label>
                            { !corporateCustomer && payQuota && useCompanyInfo?
                                <div id="enterCompanyInfo" className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                                <span>{translateService.translate('vehicleSearch1.fillInCompanyDetails')}</span>
                                  <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                                    <div >
                                      <CustomerTextInput placeholder={translateService.translate("vehicleSearch1.companyName")}
                                                         errorString={companyNameErrorString}
                                                         value={companyName}
                                                         fieldName={companyNameFieldRef}
                                                         setValue={setCompanyName}
                                                         formSubmitted={true}
                                                         className={cx(styles.r24ChildFlex1, styles.grayBorderAround, styles.r24CustomerInfoRight,
                                                             styles.vehicleSearch)} />
                                      <CustomerTextInput placeholder={translateService.translate("vehicleSearch1.companyID")}
                                                         errorString={companyIdErrorString}
                                                         value={companyId}
                                                         fieldName={companyIdFieldRef}
                                                         setValue={setCompanyId}
                                                         formSubmitted={true}
                                                         className={cx(styles.r24ChildFlex1, styles.grayBorderAround, styles.r24CustomerInfoRight,
                                                             styles.vehicleSearch, styles.r24TopMargin10)} />
                                    </div>
                                  </div>
                                </div> : null
                            }
                          </div>
                        </div> :
                        (!loggedIn && customer?.company && customer?.companyVatNumber && !customer?.mayUseQuota && !freeReservation && !paymentReselection) ?
                            <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                              <div className={styles.paymentRow}>
                                <div className={cx(styles.quotaLoginRow)}>
                                  <input type="radio" disabled={true}
                                         checked={false} readOnly
                                         className={cx(styles.r24CheckboxInput)} />
                                  <Translate content={'payment.loginForQuota'} />
                                </div>
                                <div className="clearfix">
                                  <button onClick={() => {
                                    // go back to customer info and close new customer section
                                    sessionStorage.setItem('closeNewCustomerInfo', 'true');
                                    back(1, 'existingCustomerLoginButton')}}
                                          className="btn btn-primary btn-cta btn-outline-primary w-50 float-right">
                                    {translateService.translate('rootContainer.login')}
                                  </button>
                                </div>

                              </div>
                            </div> : null
                    }

                    {canUseInvoice && !freeReservation && corporateCustomer &&
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                          <div className={styles.paymentRow}>
                            <input type="radio" name="paymentMethodsSelection" id="invoiceButton{vehicle.id}"
                                   value={paymentMethod.INVOICE} readOnly checked={payInvoice}
                                   className={cx(styles.r24CheckboxInput)}/>
                            <label htmlFor="invoiceButton{vehicle.id}" className={cx(styles.r24CheckBoxInputLabel)}>
                              <Translate content={'vehicleSearch1.invoice'} />
                            </label>
                          </div>
                        </div>
                    }

                    {!freeReservation && customerHasSavedCard &&
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                          <div className={styles.paymentRow}>
                            <div>
                              <input type="radio" name="paymentMethodsSelection" id={`savedCardButton${vehicle.id}`}
                                     checked={paySavedCard} readOnly value={paymentMethod.STORED_CARD}
                                     className={cx(styles.r24CheckboxInput)}/>
                              <label htmlFor={`savedCardButton${vehicle.id}`} style={{paddingBottom: 5}}
                                     className={cx(styles.r24CheckBoxInputLabel)}>
                                <Translate content={'vehicleSearch1.savedCreditCard'}/>
                              </label>
                            </div>
                            <SavedCardPaySelectContent
                                rootUrl={rootUrl}
                                userCardType={user?.cardType ?? ''}
                                userLastFourDigits={user?.lastFourDigits ?? ''}
                            />
                          </div>
                        </div>
                    }

                    {!freeReservation && usePaytrailFlow && <>
                        {/* Paytrail: Online Bank radio */}
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                          <div className={styles.paymentRow}>
                            <span>
                              <input type="radio" name="paymentMethodsSelection" id={`bankButton${vehicle.id}`}
                                     checked={payPaytrail} readOnly value={paymentMethod.PAYTRAIL}
                                     className={cx(styles.r24CheckboxInput)}/>
                              <label htmlFor={`bankButton${vehicle.id}`} style={{paddingBottom: 5}}
                                     className={cx(styles.r24CheckBoxInputLabel)}>
                                <Translate content={'vehicleSearch1.online'}/>
                              </label>
                            </span>
                            <div className="walley-payment-icons">
                              {['nordea', 'osuuspankki', 'danske', 'aktia',
                                'alandsbanken', 'spankki', 'pop', 'saastopankki', 'omasp'
                              ].map((logo) => (
                                <img key={logo} className="bankLogo" width="60" height="36" alt=""
                                     src={`${rootUrl}/assets/logos/banks/${logo}.svg`} loading="lazy"/>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* Paytrail: Credit Card radio */}
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                          <div className={styles.paymentRow}>
                            <span>
                              <input type="radio" name="paymentMethodsSelection" id={`credCardButton${vehicle.id}`}
                                     checked={payCreditCard} readOnly
                                     value={paymentMethod.CREDIT_CARD_INPUT_VAL}
                                     className={cx(styles.r24CheckboxInput)}/>
                              <label htmlFor={`credCardButton${vehicle.id}`} style={{paddingBottom: 5}}
                                     className={cx(styles.r24CheckBoxInputLabel)}>
                                <Translate content={'vehicleSearch1.creditCard'}/>
                              </label>
                            </span>
                            <div className={cx(styles.r24FullWidth, styles.r24AlingCenter)}>
                              <div className='p-2'>
                                <img className={'bankLogo'} width="75" height="30"
                                     src={rootUrl+'/assets/logos/banks/mastercard.svg'} alt={""} loading="lazy"/>
                                <img className={'bankLogo'} width="75" height="30"
                                     src={rootUrl+'/assets/logos/banks/visa.svg'} alt={""} loading="lazy"/>
                              </div>
                            </div>
                            {payCreditCard &&
                              <div className={'row ml-4'}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', marginBottom: '10px' }}>
                                  <input type='checkbox' name='saveCreditCardSelection' id={`saveCreditCardSelection${vehicle.id}`}
                                         checked={saveCreditCard} className={cx(styles.r24CheckboxInput)}
                                         onChange={() => {
                                           setSaveCreditCard(!saveCreditCard);
                                           changeServices({...services, saveNewCreditCard: !saveCreditCard});
                                         }}/>
                                  <label className={cx(styles.r24CheckBoxInputLabel)} htmlFor={`saveCreditCardSelection${vehicle.id}`}>
                                    <Translate content={'vehicleSearch1.saveCardForFutureUse'}/>
                                  </label>
                                </div>
                                <AddCardInfoLink hasSavedCard={customerHasSavedCard} saveNewCard={saveCreditCard}
                                                 setAdditionalPaymentInfoOpen={() => setAdditionalPaymentInfoOpen(true)} />
                                {customerHasSavedCard && saveCreditCard &&
                                  <div><Translate content={'vehicleSearch1.saveCardWillBeReplaced'}/></div>
                                }
                              </div>
                            }
                          </div>
                        </div>
                    </>}

                    {!freeReservation && !usePaytrailFlow &&
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                          <div className={styles.paymentRow}>
                            <div>
                              <input type="radio" name="paymentMethodsSelection" id={`walleyButton${vehicle.id}`}
                                     checked={payPaytrail} readOnly value={paymentMethod.PAYTRAIL}
                                     className={cx(styles.r24CheckboxInput)}/>
                              <label htmlFor={`walleyButton${vehicle.id}`} style={{paddingBottom: 5}}
                                     className={cx(styles.r24CheckBoxInputLabel)}>
                                <Translate content={'vehicleSearch1.paymentCardsAndBanks'}/>
                              </label>
                            </div>
                            <AddCardInfoLink hasSavedCard={customerHasSavedCard}
                                             saveNewCard={!!services.saveNewCreditCard}
                                             setAdditionalPaymentInfoOpen={setAdditionalPaymentInfoOpen}/>
                            <div className="walley-save-card-row">
                              <input type="checkbox" name="saveCreditCardSelection" id={`saveCardCheckbox${vehicle.id}`}
                                     checked={services.saveNewCreditCard ?? false} readOnly
                                     className={cx(styles.r24CheckboxInput)}/>
                              <label htmlFor={`saveCardCheckbox${vehicle.id}`} className={cx(styles.r24CheckBoxInputLabel)}>
                                <Translate content={'vehicleSearch1.saveCardForFutureUse'}/>
                              </label>
                            </div>
                            <div className="walley-payment-icons">
                              {['aktia', 'alandsbanken', 'danske', 'mobilepay', 'nordea',
                                'omasp', 'osuuspankki', 'pop', 'revolut', 'saastopankki', 'spankki',
                                'walley', 'mastercard', 'visa'
                              ].map((logo) => (
                                <img key={logo} className="bankLogo" width="60" height="36" alt=""
                                     src={`${rootUrl}/assets/logos/banks/${logo}.svg`} loading="lazy"/>
                              ))}
                            </div>
                          </div>
                        </div>
                    }
                    {freeReservation &&
                        <div className={cx(styles.r24FullWidth, styles.r24Padding10)}>
                          <div className={styles.paymentRow}>
                  <span>
                    <input type="radio" name="paymentMethodsSelection" id="freeButton{vehicle.id}"
                           checked={payFree} readOnly value={paymentMethod.FREE} className={styles.r24CheckboxInput}/>
                    <label htmlFor="freeButton{vehicle.id}" style={{paddingBottom: 5}} className={cx(styles.r24CheckBoxInputLabel)}>
                      <Translate content={'vehicleSearch1.free'} />
                    </label>
                  </span>
                          </div>
                        </div>
                    }
                  </div>
                  <div className={cx(styles.r24FullWidth, styles.r24Padding10Top0)}>
                    <div className={'smallCaptions text-center'}>
                      <p><strong><Translate content={'vehicleSearch1.cancellationNotice'} /></strong></p>
                      <p><Translate content={'vehicleSearch1.creditcardPaymentNotice'} /></p>
                    </div>
                  </div>
                </form>
              </div>

              {!paymentReselection && <>
              <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
              <div>
                <p style={{color: 'red', fontSize: 10, marginBottom: 5, textAlign: "start"}}>
                  {agreeErrorString ? agreeErrorString : ""}
                </p>
                <div >
                  <CustomerAgreeCheckboxInput
                      formSubmitted={paymentSubmitted}
                      label={
                            <div>
                              <div className='mb-3'>
                                {translateService.translate("vehicleSearch1.gdpr")}
                                <a target="_blank" rel="noopener noreferrer"
                                   href={"https://www.24rent.fi/tietosuojaseloste/"}>
                                  {translateService.translate("vehicleSearch1.gdpr-link")}</a>).
                                <strong>*</strong>
                              </div>
                              <div className='mb-3'>{translateService.translate("vehicleSearch1.agree")}
                                <a target="_blank" rel="noopener noreferrer"
                                   href={"https://www.24rent.fi/vuokrausehdot/"}>
                                  {translateService.translate("vehicleSearch1.agree-link")}
                                </a>{" )"}.<strong>*</strong>
                              </div>
                              <div className='mb-1'>
                                {translateService.translate("vehicleSearch1.understand")}
                                <a target="_blank" rel="noopener noreferrer"
                                   href={"https://24rent.zendesk.com/hc/fi/categories/115000177154-Mit%C3%A4-yhteisk%C3%A4ytt%C3%B6autoilu-on-"}>
                                  {translateService.translate("vehicleSearch1.understand-link")}</a>).
                                <strong>*</strong></div>
                            </div> }
                      fieldName={acceptFieldRefs} errorString={""} setValue={setPolicyConditionsAndReturn}
                      checked={false} />

                  <CustomerAgreeCheckboxInput formSubmitted={paymentSubmitted}
                                              label={<span>{translateService.translate("vehicleSearch1.fairUnderstand")}
                                                <a target="_blank" rel="noopener noreferrer"
                                                   href={'https://www.24rent.fi/en/fair-en/'}>
                                                  {translateService.translate("vehicleSearch1.fairUnderstand-link")}</a>.<strong>*</strong></span>}
                                              fieldName={"acceptField4"} errorString={""} setValue={setLiability}
                                              checked={false}/>
                </div>
              </div>
              {!user?.emailConsent && <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>}
              {!user?.emailConsent ?
                  <div className={cx(styles.r24FullWidth, styles.r24BottomMargin10)} >
                    <div id="consent{vehicle.id}" className={cx(styles.r24FullWidth, styles.emailConsentEmptyLeft)}>
                      <p className={styles.bold} style={{marginBottom: '10px'}}><Translate content={'vehicleSearch1.emailConsentQuestion'}/> *</p>
                      <div style={{display: "flex", flexDirection: "row"}}>
                        <CustomerRadioInput
                            formSubmitted={paymentSubmitted}
                            consent={"no"}
                            label={translateService.translate('vehicleSearch1.emailConsentNo')}
                            errorString={emailConsentErrorString}
                            fieldName={emailConsent1FieldRef}
                            setValue={setEmailConsentInputs}
                            value={emailConsent.no}
                        />
                        <CustomerRadioInput
                            formSubmitted={paymentSubmitted}
                            label={translateService.translate('vehicleSearch1.emailConsentYes')}
                            consent={"yes"}
                            fieldName={emailConsent2FieldRef}
                            setValue={setEmailConsentInputs}
                            value={emailConsent.yes}
                        />
                      </div>
                    </div>
                  </div> : null}
              </>}
            </>)
        }

        { saveInfoBtnClicked && !countdownExpired ?
            <div className={cx(styles.r24FullWidth, styles.r24FullWidthFlexPageSelection)} style={{alignItems: "center"}}>
              {/* This section is only for the submit error message to match + align it perfectly above the Save Information text*/}
              <div className={cx(styles.r24ChildFlex1, styles.r24RightMargin5)}></div>
              <div className={cx(styles.r24ChildFlex4, styles.r24LeftMargin5)}>
                <div className={cx(styles.r24FullWidthFlexPageSubmitError)}>{submitValidationErrorString}</div>
              </div>
            </div>
            : null
        }
      </>
      <div className={cx('d-flex min-width-320', styles.r24FullWidth, styles.r24Padding10, styles.r24FullWidthFlexPageSelection)} style={{alignItems: "center"}} hidden={countdownExpired}>
        <div className={cx('flex-grow-1', styles.r24ChildFlex1, styles.r24RightMargin5)}>
          <button type="button" className={cx("btn btn-all", styles.greenBackButton)} onClick={e=> {
            if(paymentReselection) {
              setEscapeWarningOpen?.(true);
            } else if(reservationCreated) {
              clearSelections();
              dispatch({type:"CLEAR_SELECTIONS",payload:null});
              setRedirectHome(true);
            } else {back();
              if(!authHashLogin) {
                sendGa4Event("navigate_back", { back_to: "to_customer_form" });
              } else {
                sendGa4Event("navigate_back", { back_to: "to_search_results" });
              }
            }
          }}>
            <ArrowBackIcon className={styles.arrowIcon} />
          </button>
        </div>
        <div className={cx('flex-grow-1', styles.r24ChildFlex4, styles.r24LeftMargin5)}>
          <button disabled={reservationCreated && !!sessionStorage.getItem('paymentProvider')} type="submit" className={cx("btn btn-cta", styles.orangeButton)}
                  onClick={() => {
                    setSaveInfoBtnClicked(true);
                    if(services.paymentMethod !== undefined && !companyIdErrorString && !companyNameErrorString && isCustomerInfoValid) {
                      onCreateReservation();
                    } else {
                      if (companyIdErrorString || companyNameErrorString) {
                        document.getElementById('enterCompanyInfo')?.scrollIntoView({behavior: "smooth", block: "center"});
                      } else if (!isCustomerInfoValid) {
                        if (!customer?.phoneNumber) {
                          // clear all user data as number could not be verified to save against existing customer data
                          changeUser(undefined);
                          changeCustomer(undefined);
                          sessionStorage.removeItem("customer");
                          sessionStorage.removeItem("user");
                          sessionStorage.removeItem('AT-L');
                          changeLoggedIn(false);
                        }
                        setCustomerInfoNeeded(true);
                      } else {
                        setPaymentMethodSelected(false);
                        document.getElementById('selectPaymentMethod')?.scrollIntoView({
                          behavior: "smooth",
                          block: "center"
                        });
                      }
                    }
                  }} >
            {translateService.translate(!freeReservation ? 'vehicleSearch1.reserve' : 'vehicleSearch1.freeReservation')}
          </button>
        </div>
      </div>
      {/* <ReCaptchaDisclaimer lang={language ?? 'fi'}/> */}{/* TODO: re-enable when BE reCaptcha is ready */}
    </div>
  </>
}